import mustache from "mustache";
import { ClienteFincaBaseResponse } from "../../../../modulos/terceros/response/ClienteFincaBaseResponse";
import { ClienteFincaLoteResponse } from "../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { IndiceResponse } from "../../../../modulos/catalogos/satelite/response/IndiceResponse";
import { fx_createEstadisticaLoteGrpcService } from "../../../../modulos/satelite/services/EstadisticaLoteGrpcService";
import { diasDescanso, diasOcupacion } from "../../../../utils/ganaderiaUtils";
import { _2d, _miles } from "../../../../utils/numberUtils";
import { toDateOnly } from "../../../../utils/fechaUtils";
import { IConnectRender } from "../../../definitions/IConnectRender";
import { SateliteEstadisticaChartRender } from "./SateliteEstadisticaChartRender";
import template from "./sateliteLoteSeccionTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia. */
let SECCION_ID = 0;

/** Sección de lote seleccionado del bottom-sheet de Satélite: título (+
 *  nombre/descripción del índice activo, solo bajo el breakpoint de
 *  575.8px) + datos del lote (columna izquierda, ancho mínimo, scrollable) +
 *  chart de tendencia del índice activo para ese lote (columna derecha). Sin
 *  índice activo, muestra un aviso en vez del chart. */
export class SateliteLoteSeccionRender implements IConnectRender {
  private readonly _seccionId: number;

  /**
   * @param _lote Lote seleccionado.
   * @param _finca Finca del lote (tipo agricultura/ganadería para los datos extra).
   * @param _indiceActivo Índice de la capa activa (null si ninguno tiene tile disponible).
   * @param _desde Rango de fechas activo ("yyyy-mm-dd").
   * @param _hasta Rango de fechas activo ("yyyy-mm-dd").
   */
  constructor(
    private readonly _lote: ClienteFincaLoteResponse,
    private readonly _finca: ClienteFincaBaseResponse,
    private readonly _indiceActivo: IndiceResponse | null,
    private readonly _desde: string,
    private readonly _hasta: string,
  ) {
    SECCION_ID += 1;
    this._seccionId = SECCION_ID;
  }

  render(): string {
    return mustache.render(template, {
      id: this._seccionId,
      nombreLote: this._lote.nombreLote,
      datos: this._datos(),
      tieneIndice: this._indiceActivo !== null,
      indiceNombre: this._indiceActivo?.nombre ?? "",
      indiceDescripcion: this._indiceActivo?.descripcion ?? "",
    });
  }

  /** Si hay índice activo, carga las estadísticas del lote y monta el chart
   *  filtrado por índice + rango de fechas vigente. */
  connect(): void {
    if (!this._indiceActivo) return;
    const indice = this._indiceActivo;

    fx_createEstadisticaLoteGrpcService(this._lote.id)
      .getListAsync()
      .then((estadisticas) => {
        if (this._disposed) return;

        const filtradas = estadisticas.filter((item) => {
          const fecha = toDateOnly(item.fecha);
          return (
            item.indiceId === indice.id &&
            fecha >= this._desde &&
            fecha <= this._hasta
          );
        });

        const container = document.getElementById(
          `sls-chart-${this._seccionId}`,
        );
        if (!container) return;

        const color = indice.rangoColores[indice.rangoColores.length - 1];
        this._chart = new SateliteEstadisticaChartRender(filtradas, color);
        container.innerHTML = this._chart.render();
        this._chart.connect();
      });
  }

  dispose(): void {
    this._disposed = true;
    this._chart?.dispose();
  }

  private _disposed = false;
  private _chart: SateliteEstadisticaChartRender | null = null;

  /** Área Has/Cds (todas las fincas) + Cultivo (agricultura) o
   *  Animales/Días descanso/Días ocupación (ganadería). El nombre del lote ya
   *  es el título, no se repite acá. */
  private _datos(): { label: string; value: string }[] {
    const lote = this._lote;
    const items: { label: string; value: string }[] = [
      { label: "Area Has", value: _2d(lote.areaHectareas) },
      { label: "Area Cds", value: _2d(lote.areaCuadras) },
    ];

    if (this._finca.isAgricultura) {
      items.push({ label: "Cultivo", value: lote.cultivo?.nombre ?? "-" });
    }

    if (this._finca.isGanaderia) {
      items.push(
        {
          label: "Animales",
          value:
            lote.ganaderiaCantidadAnimales > 0
              ? _miles(lote.ganaderiaCantidadAnimales)
              : "—",
        },
        { label: "Días descanso", value: this._dias(diasDescanso(lote)) },
        { label: "Días ocupación", value: this._dias(diasOcupacion(lote)) },
      );
    }

    return items;
  }

  private _dias(dias: number | null): string {
    return dias != null ? `${dias} días` : "—";
  }
}
